import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useRecordAssembly } from '@/api/assemblies'
import type { AssemblyBody } from '@/api/assemblies'
import { useBoms } from '@/api/boms'
import { useBuildable } from '@/api/buildable'
import type { Buildable, BuildableItem, BuildableLine } from '@/api/buildable'
import { useIdempotencyKey } from '@/api/idempotency'
import { useProducts } from '@/api/products'
import type { Product } from '@/api/products'
import { useVariant, useVariants } from '@/api/variants'
import { useWorkspace } from '@/api/workspace'
import { assemblyErrorMessage, negativeStockItemId, wouldGoNegative } from '@/components/assemblies/error-message'
import { selectClass } from '@/components/batches/batch-format'
import { FieldError } from '@/components/batches/batch-parts'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatQty } from '@/components/materials/pool-format'
import { ProductCombobox, SearchCombobox } from '@/components/search-combobox'
import { needsBackdateReason } from '@/components/restocks/error-message'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Record Assembly (UI-SCREENS F-10, #134), phone first: searchable pickers, 44px targets, numeric
// keyboard. Offers non-archived Variants of non-archived Products; the qty with Buildable for the
// chosen date (04g, not a cap); per BOM line of the BOM in effect on that date, the expected qty
// (BOM × qty) and the actual qty: a Model line prefilled over its matching WIP items from the
// largest balance first (#126 Q13), with substitutes picked behind a warning naming the differing
// slots (Q14); a component line from its Pool, which may go negative (Q16). Then labor minutes,
// the business date and a note. WIP that would go negative blocks the form, with a shortcut to a
// WIP Adjustment (04l #140). The server re-checks all of it under its locks.
const searchSchema = z.object({ variantId: z.coerce.number().int().positive().optional().catch(undefined) })

export const Route = createFileRoute('/w/$slug/assemblies/new')({
  component: NewAssembly,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'assemblies.form.title' },
})

function NewAssembly() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const { variantId } = Route.useSearch()
  const workspace = useWorkspace(slug)
  const products = useProducts(slug)
  const initialVariant = useVariant(slug, variantId ?? 0, { enabled: variantId !== undefined })
  const waitingForVariant = variantId !== undefined && initialVariant.isPending

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/assemblies" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('assemblies.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{t('assemblies.form.title')}</h1>

      {workspace.isPending || products.isPending || waitingForVariant ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : !workspace.data || !products.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : !workspace.data.adopted_on ? (
        <Card>
          <CardContent className="py-4">
            <p role="status" className="text-sm">
              {t('restocks.errors.adoptionDateNotSet')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <AssemblyForm
          slug={slug}
          adoptedOn={workspace.data.adopted_on}
          timezone={workspace.data.timezone}
          products={products.data.filter((product) => !product.archived_at)}
          initialProductId={variantId && initialVariant.data ? String(initialVariant.data.product_id) : ''}
          initialVariantId={variantId && initialVariant.data && !initialVariant.data.archived_at ? String(variantId) : ''}
        />
      )}
    </section>
  )
}

// Today in the Workspace's timezone: the business date may never be after it (domain-rules).
function todayIn(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date())
}

const isWhole = (value: string) => /^\d+$/.test(value)

// One Assembly line: the stock item used and the qty actually used (a string while typed).
type Row = { stockItemId: number; actual: string; substitute: boolean }
// The rows of each BOM line, by bom_line_id.
type Rows = Record<number, Row[]>

// Expected = BOM qty × qty, prefilled in full: a Model line is spread over its matching items,
// largest balance first, and whatever they can't cover goes on the last of them, so that row shows
// the WIP-short block (with the Adjustment shortcut) until the WIP is corrected or the actual qty
// is lowered on purpose for breakage. A component line takes its Pool.
function prefill(buildable: Buildable | undefined, qty: number): Rows {
  const rows: Rows = {}
  for (const line of buildable?.lines ?? []) {
    const expected = line.qty_per_unit * qty
    if (line.kind === 'component') {
      rows[line.bom_line_id] = line.stock_item_id
        ? [{ stockItemId: line.stock_item_id, actual: String(expected), substitute: false }]
        : []
      continue
    }
    let remaining = expected
    const takes = line.matching_items.map((item) => {
      const take = Math.min(item.qty, remaining)
      remaining -= take
      return { item, take }
    })
    if (takes.length > 0) takes[takes.length - 1].take += remaining
    rows[line.bom_line_id] = takes.map(({ item, take }) => ({
      stockItemId: item.stock_item_id,
      actual: String(take),
      substitute: false,
    }))
  }
  return rows
}

// The WIP item a row draws from, from Buildable's lists (qty > 0 only). A matching item has no
// differing slots.
function itemOf(line: BuildableLine, row: Row): BuildableItem | undefined {
  const items: BuildableItem[] = [
    ...line.matching_items.map((item) => ({ ...item, differing_slot_ids: [] })),
    ...line.substitute_items,
  ]
  return items.find((item) => item.stock_item_id === row.stockItemId)
}

function AssemblyForm({
  slug,
  adoptedOn,
  timezone,
  products,
  initialProductId,
  initialVariantId,
}: {
  slug: string
  adoptedOn: string
  timezone: string
  products: Product[]
  initialProductId: string
  initialVariantId: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const record = useRecordAssembly()
  const idempotency = useIdempotencyKey()
  const [askReason, setAskReason] = useState(false)
  const today = todayIn(timezone)
  const required = t('assemblies.form.required')

  const formSchema = z.object({
    productId: z.string().min(1, { message: required }),
    variantId: z.string().min(1, { message: required }),
    qty: z.string().refine((value) => isWhole(value) && Number(value) > 0, { message: t('assemblies.form.qtyInvalid') }),
    laborMinutes: z.string().refine(isWhole, { message: t('assemblies.form.wholeNumber') }),
    occurredOn: z.string().min(1, { message: required }),
    note: z.string(),
    backdateReason: z.string(),
  })
  type FormValues = z.infer<typeof formSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      productId: products.some((product) => String(product.id) === initialProductId) ? initialProductId : '',
      variantId: initialVariantId,
      qty: '1',
      laborMinutes: '',
      occurredOn: today,
      note: '',
      backdateReason: '',
    },
  })
  const [productId, variantId, qty, occurredOn] = useWatch({
    control: form.control,
    name: ['productId', 'variantId', 'qty', 'occurredOn'],
  })

  const variants = useVariants(slug, Number(productId), { enabled: productId !== '' })
  const boms = useBoms(slug, Number(productId), { enabled: productId !== '' })
  // Variants that need color on a Model of the BOM in effect on the date aren't offered (#126 Q15);
  // Buildable's `needs_color` still blocks one reached through ?variantId= or a stale list.
  const bomOnDate = (boms.data ?? [])
    .filter((bom) => bom.effective_from <= occurredOn)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0]
  const bomModelIds = (bomOnDate?.lines ?? []).flatMap((line) => (line.model_id === null ? [] : [line.model_id]))
  const activeVariants = (variants.data ?? []).filter(
    (variant) =>
      !variant.archived_at &&
      (String(variant.id) === variantId ||
        !variant.slots.some((slot) => slot.needs_color && bomModelIds.includes(slot.model_id))),
  )
  const qtyCount = isWhole(qty) && Number(qty) > 0 ? Number(qty) : 0
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(occurredOn)
  const buildable = useBuildable(slug, Number(variantId), occurredOn, { enabled: variantId !== '' && dateValid })
  const data = buildable.data

  // The rows follow the prefill until edited; a new Variant, date or qty, or a Buildable answer
  // that changed (a refetch with the same figures keeps its reference), starts them over. No
  // effect needed: the edited rows are kept only for the inputs they were made for.
  const prefillKey = `${variantId}|${occurredOn}|${qtyCount}`
  const [edited, setEdited] = useState<{ key: string; data: Buildable | undefined; rows: Rows } | null>(null)
  const [picking, setPicking] = useState<{ bomLineId: number; stockItemId: number } | null>(null)
  const rows = edited?.key === prefillKey && edited.data === data ? edited.rows : prefill(data, qtyCount)
  const setRows = (next: Rows) => setEdited({ key: prefillKey, data, rows: next })
  const setActual = (bomLineId: number, stockItemId: number, actual: string) =>
    setRows({
      ...rows,
      [bomLineId]: (rows[bomLineId] ?? []).map((row) => (row.stockItemId === stockItemId ? { ...row, actual } : row)),
    })
  const addSubstitute = (bomLineId: number, stockItemId: number) => {
    setRows({ ...rows, [bomLineId]: [...(rows[bomLineId] ?? []), { stockItemId, actual: '0', substitute: true }] })
    setPicking(null)
  }

  const lines = data?.lines ?? []
  const unavailable = !data || data.reason !== null || data.needs_color
  const noRows = lines.some((line) => (rows[line.bom_line_id] ?? []).length === 0)
  const badNumber = lines.some((line) => (rows[line.bom_line_id] ?? []).some((row) => !isWhole(row.actual)))
  const shortRows = lines.flatMap((line) =>
    line.kind === 'model'
      ? (rows[line.bom_line_id] ?? []).filter(
          (row) => isWhole(row.actual) && Number(row.actual) > (itemOf(line, row)?.qty ?? 0),
        )
      : [],
  )
  const wipShort = shortRows.length > 0
  // The WIP item the Adjustment shortcut opens I-11 with (04l #140): the first short row's, or the
  // one the server refused. None when a Model line has no WIP item at all.
  const shortItemId = shortRows[0]?.stockItemId ?? (record.isError ? negativeStockItemId(record.error) : undefined)
  const blocked = unavailable || qtyCount === 0 || noRows || badNumber || wipShort
  const showAdjustmentShortcut = wipShort || noRows || (record.isError && wouldGoNegative(record.error))

  const onSubmit = form.handleSubmit((values) => {
    if (blocked) return
    const reason = values.backdateReason.trim()
    const note = values.note.trim()
    // Rows with a qty, and at least one row per BOM line (a line used 0 times is still recorded).
    const payloadLines = lines.flatMap((line) => {
      const lineRows = rows[line.bom_line_id] ?? []
      const used = lineRows.filter((row) => Number(row.actual) > 0)
      return (used.length > 0 ? used : lineRows.slice(0, 1)).map((row) => ({
        stock_item_id: row.stockItemId,
        actual_qty: Number(row.actual),
      }))
    })
    const body: AssemblyBody = {
      assembly: {
        variant_id: Number(values.variantId),
        qty: Number(values.qty),
        labor_minutes: Number(values.laborMinutes),
        occurred_on: values.occurredOn,
        ...(note === '' ? {} : { note }),
        ...(reason === '' ? {} : { backdate_reason: reason }),
        lines: payloadLines,
      },
    }
    record.mutate(
      { params: { path: { slug }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: (created) =>
          navigate({ to: '/w/$slug/assemblies/$assemblyId', params: { slug, assemblyId: String(created.id) } }),
        onSettled: (_created, error) => {
          if (needsBackdateReason(error)) setAskReason(true)
          idempotency.settle(error)
        },
      },
    )
  })

  const errors = form.formState.errors

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {record.isError ? assemblyErrorMessage(t, record.error) : ''}
      </p>

      <div className="flex flex-col gap-2">
        <Label htmlFor="productId">{t('assemblies.form.product')}</Label>
        <ProductCombobox
          id="productId"
          value={productId}
          products={products}
          invalid={Boolean(errors.productId)}
          onChange={(value) => {
            form.setValue('productId', value, { shouldValidate: form.formState.isSubmitted })
            form.setValue('variantId', '')
          }}
        />
        <FieldError message={errors.productId?.message} />
      </div>

      {productId ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="variantId">{t('assemblies.form.variant')}</Label>
          <SearchCombobox
            id="variantId"
            value={variantId}
            options={activeVariants.map((row) => ({ value: String(row.id), code: row.sku, name: row.name }))}
            invalid={Boolean(errors.variantId)}
            placeholder={t('searchCombobox.variant.placeholder')}
            searchPlaceholder={t('searchCombobox.variant.search')}
            onChange={(value) => form.setValue('variantId', value, { shouldValidate: form.formState.isSubmitted })}
          />
          <FieldError message={errors.variantId?.message} />
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor="qty">{t('assemblies.form.qty')}</Label>
          <Input id="qty" inputMode="numeric" className="h-11" {...form.register('qty')} />
          <FieldError message={errors.qty?.message} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="occurredOn">{t('assemblies.form.date')}</Label>
          <Input id="occurredOn" type="date" className="h-11" min={adoptedOn} max={today} {...form.register('occurredOn')} />
          <FieldError message={errors.occurredOn?.message} />
        </div>
      </div>
      <p className="text-sm text-muted-foreground">{t('assemblies.form.dateHint')}</p>

      {variantId && data ? (
        data.reason !== null ? (
          <p role="alert" className="text-sm text-destructive">
            {t(`assemblies.buildableReason.${data.reason}`)}
          </p>
        ) : data.needs_color ? (
          <p role="alert" className="text-sm text-destructive">
            {t('assemblies.errors.variantNeedsColor')}
          </p>
        ) : (
          <div className="flex flex-col gap-1 text-sm">
            <p>
              {t('assemblies.form.buildable', { count: data.buildable ?? 0 })}
              {(data.with_substitutes ?? 0) > (data.buildable ?? 0) ? (
                <span className="text-muted-foreground">
                  {' '}
                  {t('assemblies.form.withSubstitutes', { count: (data.with_substitutes ?? 0) - (data.buildable ?? 0) })}
                </span>
              ) : null}
            </p>
            <p className="text-muted-foreground">{t('assemblies.form.buildableHint')}</p>
          </div>
        )
      ) : variantId && buildable.isPending && dateValid ? (
        <LoadingSkeleton variant="lines" rows={2} />
      ) : null}

      {variantId && data && !unavailable ? (
        <div className="flex flex-col gap-3">
          <h2 className="font-heading text-base font-semibold">{t('assemblies.form.lines')}</h2>
          <p className="text-sm text-muted-foreground">{t('assemblies.form.linesHint')}</p>
          {lines.map((line) => (
            <LineCard
              key={line.bom_line_id}
              line={line}
              expected={line.qty_per_unit * qtyCount}
              rows={rows[line.bom_line_id] ?? []}
              picking={picking?.bomLineId === line.bom_line_id ? picking.stockItemId : null}
              onActual={(stockItemId, actual) => setActual(line.bom_line_id, stockItemId, actual)}
              onPick={(stockItemId) => setPicking(stockItemId ? { bomLineId: line.bom_line_id, stockItemId } : null)}
              onConfirm={(stockItemId) => addSubstitute(line.bom_line_id, stockItemId)}
            />
          ))}
        </div>
      ) : null}

      {showAdjustmentShortcut ? (
        <Button asChild variant="outline" className="h-11 self-start">
          <Link to="/w/$slug/adjustments/new" params={{ slug }} search={{ location: 'wip', itemId: shortItemId }}>
            {t('assemblies.form.toAdjustment')}
          </Link>
        </Button>
      ) : null}

      <div className="flex flex-col gap-2">
        <Label htmlFor="laborMinutes">{t('assemblies.form.laborMinutes')}</Label>
        <Input id="laborMinutes" inputMode="numeric" className="h-11" {...form.register('laborMinutes')} />
        <p className="text-sm text-muted-foreground">{t('assemblies.form.laborMinutesHint')}</p>
        <FieldError message={errors.laborMinutes?.message} />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="note">{t('assemblies.form.note')}</Label>
        <Input id="note" className="h-11" {...form.register('note')} />
      </div>

      {askReason ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="backdateReason">{t('restocks.form.backdateReason')}</Label>
          <Input id="backdateReason" className="h-11" {...form.register('backdateReason')} />
          <p className="text-sm text-muted-foreground">{t('restocks.form.backdateReasonHint')}</p>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11" disabled={record.isPending || blocked}>
          {t(record.isPending ? 'assemblies.form.saving' : 'assemblies.form.save')}
        </Button>
        <Button asChild variant="ghost" className="h-11">
          <Link to="/w/$slug/assemblies" params={{ slug }}>
            {t('assemblies.form.cancel')}
          </Link>
        </Button>
      </div>
    </form>
  )
}

// One BOM line: the expected qty and a row per stock item used. A Model line lists its WIP items
// (colors, balance, actual qty) and offers substitutes behind a warning; a component line its Pool.
function LineCard({
  line,
  expected,
  rows,
  picking,
  onActual,
  onPick,
  onConfirm,
}: {
  line: BuildableLine
  expected: number
  rows: Row[]
  picking: number | null
  onActual: (stockItemId: number, actual: string) => void
  onPick: (stockItemId: number | null) => void
  onConfirm: (stockItemId: number) => void
}) {
  const { t } = useTranslation()
  const unit = line.unit ? ` ${line.unit}` : ''
  const offered = line.substitute_items.filter((item) => !rows.some((row) => row.stockItemId === item.stock_item_id))
  const picked = line.substitute_items.find((item) => item.stock_item_id === picking)

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-medium">{line.label}</span>
          <span className="text-sm text-muted-foreground">
            {t('assemblies.form.expected', { qty: `${formatQty(expected)}${unit}` })}
          </span>
        </div>

        {line.kind === 'component'
          ? rows.map((row) => (
              <div key={row.stockItemId} className="flex flex-col gap-2">
                <Label htmlFor={`actual-${row.stockItemId}`}>{t('assemblies.form.actual')}</Label>
                <Input
                  id={`actual-${row.stockItemId}`}
                  inputMode="numeric"
                  className="h-11"
                  value={row.actual}
                  onChange={(event) => onActual(row.stockItemId, event.target.value)}
                />
                <span className="text-sm text-muted-foreground">
                  {t('assemblies.form.available', { qty: `${formatQty(line.available)}${unit}` })}
                </span>
                {!isWhole(row.actual) ? <FieldError message={t('assemblies.form.wholeNumber')} /> : null}
                {isWhole(row.actual) && Number(row.actual) > line.available ? (
                  <p className="text-sm text-muted-foreground">{t('assemblies.form.componentNegative')}</p>
                ) : null}
              </div>
            ))
          : null}

        {line.kind === 'model' && rows.length === 0 ? (
          <p role="alert" className="text-sm text-destructive">
            {t('assemblies.form.noItems')}
          </p>
        ) : null}

        {line.kind === 'model'
          ? rows.map((row) => {
              const item = itemOf(line, row)
              const available = item?.qty ?? 0
              return (
                <div key={row.stockItemId} className="flex flex-col gap-2 rounded-lg border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <ul className="text-sm">
                      {(item?.colors ?? []).map((color) => (
                        <li key={color.model_slot_id}>
                          {color.label}: {color.pool.label}
                        </li>
                      ))}
                    </ul>
                    {row.substitute ? <Badge variant="outline">{t('assemblies.form.substituteBadge')}</Badge> : null}
                  </div>
                  <Label htmlFor={`actual-${row.stockItemId}`}>{t('assemblies.form.actual')}</Label>
                  <Input
                    id={`actual-${row.stockItemId}`}
                    inputMode="numeric"
                    className="h-11"
                    value={row.actual}
                    onChange={(event) => onActual(row.stockItemId, event.target.value)}
                  />
                  <span className="text-sm text-muted-foreground">
                    {t('assemblies.form.available', { qty: formatQty(available) })}
                  </span>
                  {!isWhole(row.actual) ? <FieldError message={t('assemblies.form.wholeNumber')} /> : null}
                  {isWhole(row.actual) && Number(row.actual) > available ? (
                    <FieldError message={t('assemblies.form.wipShort', { qty: formatQty(available) })} />
                  ) : null}
                </div>
              )
            })
          : null}

        {line.kind === 'model' && offered.length > 0 && !picked ? (
          <div className="flex flex-col gap-2">
            <Label htmlFor={`substitute-${line.bom_line_id}`}>{t('assemblies.form.addSubstitute')}</Label>
            <select
              id={`substitute-${line.bom_line_id}`}
              className={selectClass}
              value=""
              onChange={(event) => onPick(event.target.value ? Number(event.target.value) : null)}
            >
              <option value="">{t('assemblies.form.selectSubstitute')}</option>
              {offered.map((item) => (
                <option key={item.stock_item_id} value={String(item.stock_item_id)}>
                  {item.colors.map((color) => `${color.label}: ${color.pool.label}`).join(', ')} ({formatQty(item.qty)})
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {picked ? (
          <div role="alert" className="flex flex-col gap-2 rounded-lg border border-destructive/50 p-3">
            <p className="text-sm">
              {t('assemblies.form.substituteWarning', {
                slots: picked.colors
                  .filter((color) => picked.differing_slot_ids.includes(color.model_slot_id))
                  .map((color) => color.label)
                  .join(', '),
              })}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" className="h-11" onClick={() => onConfirm(picked.stock_item_id)}>
                {t('assemblies.form.confirmSubstitute')}
              </Button>
              <Button type="button" variant="ghost" className="h-11" onClick={() => onPick(null)}>
                {t('assemblies.form.cancelSubstitute')}
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
